import { Box } from '@mui/material';
import { DisplayAlertErrors, FileType, FileUploader, TUploadFile } from '@verifime/components';
import { useCallback, useEffect, useState } from 'react';
import { useFileUploaderContext } from './FileUploaderContext';
import { useFormContext } from './FormContext';
import { usePhotoGuideContext } from './PhotoGuideContext';
import { useScanContext } from './ScanContext';
import { entityDocumentApi } from './utils';
import { useWizardContext } from './WizardContext';
import { DocumentType } from '@verifime/utils';

// A driver licence and a photo card each take up to two files: the upload instruction for both asks
// for the front and back of the card.
const DOCUMENT_TYPES_WITH_TWO_SIDES: string[] = [
  DocumentType.DriverLicence,
  DocumentType.PhotoCard,
];

/**
 * Due to backend design, entity document upload needs two steps:
 * 
 * 1. Call the endpoint `POST /v1/file/presign` to get the presign upload url (let's say fileUploadUrl)
 * 2. Call `
 *  fetch(fileUploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': fileType },
      body: file,
    })
 * ` to upload the file
 * 
*/
export const FileUploaderView = ({ currentStep }: Readonly<{ currentStep: number }>) => {
  const [initialFiles, setInitialFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingInitialFiles, setIsLoadingInitialFiles] = useState(false);

  const {
    state: { customerId },
    getSelectedDocumentTypeForStep,
  } = useWizardContext();

  const {
    setFileIds,
    setScanFilesId,
    resetFileUploader,
    state: { fileMutationId },
  } = useFileUploaderContext();

  const { setFormData, resetForm } = useFormContext();
  const { appendFileIds } = useFileUploaderContext();
  const {
    state: { scanStatus },
    startScan,
    resetScanStatus,
    cancelScan,
  } = useScanContext();
  const { setIsShowCompactPhotoGuidance } = usePhotoGuideContext();

  const documentType = getSelectedDocumentTypeForStep(currentStep);

  const fetchInitialFiles = useCallback(() => {
    if (!documentType || !customerId) {
      return;
    }

    setIsLoadingInitialFiles(true);
    entityDocumentApi
      .fetchInitialFiles({ entityId: customerId, documentType: documentType })
      .then((data) => {
        setFileIds((data?.uploadedFiles || []).map((file) => file.id));
        setScanFilesId(data.additionalDetails?.scanFilesId || null);
        if (!data) {
          setInitialFiles([]);
          return;
        }
        setInitialFiles(data.uploadedFiles || []);
      })
      .catch((error) => setErrorMessage(error?.message || 'Unknown errors'))
      .finally(() => {
        setIsLoadingInitialFiles(false);
      });
  }, [customerId, documentType]);

  useEffect(() => {
    if (!documentType) {
      return;
    }
    fetchInitialFiles();
  }, [fetchInitialFiles, documentType]);

  useEffect(() => {
    // Refetch or reload preview files when fileMutationId changes
    if (fileMutationId) {
      fetchInitialFiles();
    }
  }, [fileMutationId, fetchInitialFiles]);

  // Clear error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleUploadSuccess = ({
    uploadedFiles,
    additionalDetails,
  }: {
    uploadedFiles: TUploadFile[];
    additionalDetails?: Record<string, any>;
  }) => {
    setScanFilesId(additionalDetails.scanFilesId);

    appendFileIds(uploadedFiles.map((file) => file.id));
    startScan();
  };

  const handleFileRemove = (_: TUploadFile, uploadedFiles: TUploadFile[], extraData: any) => {
    setScanFilesId(extraData?.scanFilesId);

    // We'd like to show the photo guidance again if:
    // 1. No files are uploaded after removal
    // 2. The previous scan was failed
    if (scanStatus !== 'FAILED') {
      resetScanStatus();
    } else {
      setIsShowCompactPhotoGuidance(true);
    }
    cancelScan();

    if (uploadedFiles.length > 0) {
      setFileIds(uploadedFiles.map((file) => file.id));
      return;
    }

    resetFileUploader();
    resetForm();
  };

  // Show nothing and waiting for initial files to load to avoid racing condition and flickering when loading initial files,
  // because the FileUploader will show its own loading state
  if (isLoadingInitialFiles) {
    return null;
  }

  if (!documentType) {
    return null;
  }

  return (
    <Box data-component="FileUploaderView">
      {errorMessage && <DisplayAlertErrors errors={[{ message: errorMessage }]} />}
      <FileUploader
        key={`${documentType}-${fileMutationId}`}
        maxFileCount={DOCUMENT_TYPES_WITH_TWO_SIDES.includes(documentType) ? 2 : 1}
        hideUploaderAfterUpload
        hideUploaderOnInitialFiles
        acceptedFileTypes={[FileType.JPEG, FileType.PNG, FileType.PDF]}
        onUpload={(files, onProgress) =>
          entityDocumentApi.upload(files as File[], onProgress, customerId, documentType)
        }
        onUploadSuccess={handleUploadSuccess}
        onBeforeUpload={() => {
          setFormData(null);
          return true;
        }}
        initialFiles={initialFiles}
        onFileRemove={handleFileRemove}
        onDelete={async (fileToBeDelete, uploadedFiles) => {
          await entityDocumentApi.delete(fileToBeDelete.id, customerId, documentType);
          setInitialFiles(uploadedFiles);
        }}
      />
    </Box>
  );
};
